import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
} from "@learn-me/ui";
import {
  Upload,
  X,
  FileText,
  Image,
  Loader2,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { adminApi, type ReferenceDoc } from "../api/adminApi";
import toast from "react-hot-toast";

interface ReferenceDocsPanelProps {
  courseId: string;
}

export function ReferenceDocsPanel({ courseId }: ReferenceDocsPanelProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["course-reference-docs", courseId],
    queryFn: () => adminApi.getCourseReferenceDocs(courseId),
  });

  const addDocMutation = useMutation({
    mutationFn: (file: File) => adminApi.addCourseReferenceDoc(courseId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["course-reference-docs", courseId],
      });
    },
    onError: () => {
      toast.error("Error al subir el documento");
    },
  });

  const deleteDocMutation = useMutation({
    mutationFn: (docId: string) =>
      adminApi.deleteCourseReferenceDoc(courseId, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["course-reference-docs", courseId],
      });
      toast.success("Documento eliminado");
    },
    onError: () => {
      toast.error("Error al eliminar el documento");
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: (docId: string) =>
      adminApi.reprocessReferenceDoc(courseId, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["course-reference-docs", courseId],
      });
      toast.success("Documento reprocesado");
    },
    onError: () => {
      toast.error("Error al reprocesar el documento");
    },
  });

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const uploadFile = async (file: File) => {
    const fileId = `${Date.now()}-${file.name}`;
    setUploadingFiles((prev) => [...prev, fileId]);

    try {
      await addDocMutation.mutateAsync(file);
      toast.success(`${file.name} subido correctamente`);
    } finally {
      setUploadingFiles((prev) => prev.filter((id) => id !== fileId));
    }
  };

  const processFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    for (const file of files) {
      await uploadFile(file);
    }
  };

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    await processFiles(e.dataTransfer.files);
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      await processFiles(e.target.files);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type: "PDF" | "IMAGE") => {
    return type === "PDF" ? (
      <FileText className="h-5 w-5 text-red-500" />
    ) : (
      <Image className="h-5 w-5 text-blue-500" />
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Documentos de Referencia
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Sube PDFs e imágenes como material de referencia. La IA los analizará
          para generar contenido más preciso para los capítulos y quizzes.
        </p>

        {/* Drop zone */}
        <div
          className={`
            border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
            ${isDragging ? "border-primary-500 bg-primary-50" : "border-muted-foreground/25 hover:border-muted-foreground/50"}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />

          <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm font-medium mb-1">
            Arrastra archivos aquí o haz clic para seleccionar
          </p>
          <p className="text-xs text-muted-foreground">
            PDFs e imágenes (máx. 50MB para PDFs, 10MB para imágenes)
          </p>
        </div>

        {/* Uploading files */}
        {uploadingFiles.length > 0 && (
          <div className="space-y-2">
            {uploadingFiles.map((fileId) => (
              <div
                key={fileId}
                className="flex items-center gap-3 p-3 bg-muted rounded-lg"
              >
                <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
                <span className="text-sm">Subiendo y procesando...</span>
              </div>
            ))}
          </div>
        )}

        {/* Documents list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : docs.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-4">
            No hay documentos de referencia
          </p>
        ) : (
          <div className="space-y-2">
            {docs.map((doc) => (
              <DocumentItem
                key={doc.id}
                doc={doc}
                onDelete={() => deleteDocMutation.mutate(doc.id)}
                onReprocess={() => reprocessMutation.mutate(doc.id)}
                isDeleting={deleteDocMutation.isPending}
                isReprocessing={reprocessMutation.isPending}
              />
            ))}
          </div>
        )}

        {/* Document count */}
        {docs.length > 0 && (
          <p className="text-xs text-muted-foreground text-center">
            {docs.length} documento{docs.length !== 1 ? "s" : ""} •{" "}
            {docs.filter((d) => d.isProcessed).length} procesado
            {docs.filter((d) => d.isProcessed).length !== 1 ? "s" : ""}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface DocumentItemProps {
  doc: ReferenceDoc;
  onDelete: () => void;
  onReprocess: () => void;
  isDeleting: boolean;
  isReprocessing: boolean;
}

function DocumentItem({
  doc,
  onDelete,
  onReprocess,
  isDeleting,
  isReprocessing,
}: DocumentItemProps) {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg">
      {doc.type === "PDF" ? (
        <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />
      ) : (
        <Image className="h-5 w-5 text-blue-500 flex-shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{doc.name}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{formatFileSize(doc.size)}</span>
          <span>•</span>
          {doc.isProcessed ? (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle className="h-3 w-3" />
              Procesado
            </span>
          ) : (
            <span className="flex items-center gap-1 text-amber-600">
              <AlertCircle className="h-3 w-3" />
              Sin procesar
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1">
        {!doc.isProcessed && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReprocess}
            disabled={isReprocessing}
            title="Reprocesar documento"
          >
            {isReprocessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={isDeleting}
          title="Eliminar documento"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4 text-destructive" />
          )}
        </Button>
      </div>
    </div>
  );
}
